import { CampaignResponse, Conditions, Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import {
    ApplyContext,
    ConflictItem,
    FetchContext,
    FetchParams,
    OrderContext,
    SubmitError,
    ValidationError,
} from '../types';
import { CampaignStrategy } from './campaign-strategy';

const CONDITION_FIELD = 'QUANTIDADE_ITEM';
const PAYMENT_CONDITION_BONIFICACAO = '100';

const getConditionItems = (campaign: CampaignResponse): Conditions[] =>
    campaign.condicoes.filter((c) => c.campo === CONDITION_FIELD);

export class BonificacaoFixaStrategy implements CampaignStrategy {
    readonly tipo = 'BONIFICACAO_FIXA' as const;

    isAvailableForOrder(
        _campaign: CampaignResponse,
        _context: OrderContext,
    ): boolean {
        return true;
    }

    validateEligibility(
        campaign: CampaignResponse,
        availableItems: ItemResponse[],
    ): ValidationError[] {
        const errors: ValidationError[] = [];
        for (const cond of getConditionItems(campaign)) {
            const item = availableItems.find(
                (i) => i.number === cond.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'CONDITION_ITEM_MISSING',
                    message: `SKU ${cond.numeroItem} (condição) não disponível`,
                    numeroItem: cond.numeroItem,
                });
            }
        }
        return errors;
    }

    detectConflicts(
        campaign: CampaignResponse,
        otherSelected: CampaignResponse[],
    ): ConflictItem[] {
        const conflicts: ConflictItem[] = [];
        const ownNumbers = new Set<string>([
            ...getConditionItems(campaign).map((c) => c.numeroItem),
            ...campaign.resultados.map((r) => r.numeroItem).filter(Boolean),
        ]);

        for (const other of otherSelected) {
            if (other.id === campaign.id) continue;
            const otherNumbers = [
                ...other.condicoes
                    .filter((c) => c.campo === 'QUANTIDADE_ITEM' || c.campo === 'ATINGIR_COTA')
                    .map((c) => c.numeroItem),
                ...other.resultados.map((r) => r.numeroItem).filter(Boolean),
            ];

            for (const n of otherNumbers) {
                if (ownNumbers.has(n)) {
                    conflicts.push({
                        numeroItem: n,
                        campaignIds: [campaign.id, other.id],
                    });
                }
            }
        }

        return conflicts;
    }

    onEnable(
        _campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        return orderItems;
    }

    applyResultToItem(
        item: ItemResponse,
        result: Result,
        campaign: CampaignResponse,
        _context: ApplyContext,
    ): ItemResponse {
        return {
            ...item,
            campaign,
            result,
            disabled: true,
            quantity: Number(result.qtd) || 0,
            condition: undefined,
        };
    }

    recalculateOnQuantityChange(
        _campaign: CampaignResponse,
        orderItems: ItemResponse[],
        _context: ApplyContext,
    ): ItemResponse[] {
        return orderItems;
    }

    validateForSubmit(): SubmitError[] {
        return [];
    }

    requiresExtraFetchParams(
        _campaign: CampaignResponse,
        _context: FetchContext,
    ): Partial<FetchParams> {
        return {};
    }

    acceptsPaymentCondition(paymentConditionId: string): boolean {
        return paymentConditionId === PAYMENT_CONDITION_BONIFICACAO;
    }
}
